import _ from 'lodash';
import { getUrlParameter } from 'helpers/ContentHelper';
import { getCommonTealiumProps } from 'helpers/AccountHelper';
import { Events, SegmentPageProfileTypes, handleAnalytics } from '@foxcorp/delta-web-analytics';
import { isValidEmail, passwordErrors } from 'helpers/Account';
import { getActivateCodeFromURL, getActivateCode } from 'helpers/ActivateHelper';

const EMAIL_INVALID = '400';

export const onSignin = ({
  email,
  handleActivateCode,
  headerProps,
  login,
  password,
  setIsProcessing,
  setGenericError,
  setPasswordError,
  signInCompleted,
  signInSuccess,
  utagAccountSource,
}) => {
  const { onClose } = headerProps;
  setIsProcessing(true);

  const handleSuccess = account => {
    if (signInCompleted) signInCompleted({ utagAccountSource });
    if (!getUrlParameter('code'))
      handleAnalytics({
        event: Events.PROFILE_SIGNIN_COMPLETED,
        payload: {
          account,
          page_profile_type: SegmentPageProfileTypes.GENERAL,
          page_start_source: utagAccountSource,
        },
        tealiumPayload: {
          ...getCommonTealiumProps(),
        },
      });
    if (handleActivateCode({ ...account, isNew: false })) return;
    if (signInSuccess) {
      signInSuccess();
    }
    onClose();
  };

  setSigninErrors(
    hasErrors => {
      if (hasErrors) {
        setIsProcessing(false);
      }
      if (!hasErrors && login) {
        const body = {
          password,
          email,
        };
        login(body, result => {
          setIsProcessing(false);
          const { error, account } = result;
          if (error) {
            handleAnalytics({
              event: Events.PROFILE_SIGN_IN_ERROR,
              payload: {
                page_error_description: error.message || 'no error description',
                page_error_type: 'server-side',
                page_start_source: utagAccountSource,
              },
            });
            return setGenericError(error.message);
          }
          return handleSuccess(account);
        });
      }
    },
    {
      setPasswordError,
      email,
      password,
      utagAccountSource,
    },
  );
};

const setSigninErrors = (cb, name) => {
  const { email, password, setPasswordError, utagAccountSource } = name;
  const signinPasswordError = passwordErrors(password);
  const signinEmailError = !isValidEmail(email);

  const setStateCb = () => {
    const hasErrors = signinPasswordError || signinEmailError || false;
    if (hasErrors) {
      let errorDescription;
      if (signinPasswordError) {
        errorDescription = 'Password Error';
      } else if (signinEmailError) {
        errorDescription = 'Email Error';
      } else {
        errorDescription = 'no error description';
      }

      handleAnalytics({
        event: Events.PROFILE_SIGN_IN_ERROR,
        payload: {
          page_error_description: errorDescription,
          page_error_type: 'client-side',
          page_start_source: utagAccountSource,
        },
      });
    }
    if (cb) return cb(hasErrors);
    return hasErrors;
  };

  if (signinPasswordError) setPasswordError(signinPasswordError);

  return setStateCb();
};

export const onConsolidatedEmailSignIn = _.debounce(
  async ({ checkStatus, email, setExistingEmail }) => {
    const status = (await checkStatus({ userEmail: email })) || {};
    if (status?.found) {
      setExistingEmail(true);
    } else {
      setExistingEmail(false);
    }
  },
  500,
);

export const resetPassword = ({
  reset,
  email,
  setScreen,
  forgotPasswordPage,
  existingUserPage,
}) => {
  const body = {
    email,
  };
  reset(body, () => setScreen({ current: forgotPasswordPage, prev: existingUserPage }));
};

export const onSignup = ({
  register,
  signUpCompleted,
  email,
  password,
  utagAccountSource,
  handleActivateCode,
  toggleSignupSuccess,
  setIsProcessing,
  language: { profileCreate_invalidEmailText: invalidEmailError },
  anonymousAccount,
  signupSuccess,
  setGenericError,
  setPasswordError,
}) => {
  const fbAuthStarted = false;
  setIsProcessing(true);
  const handleSuccess = newAccount => {
    const newsLetterVal = 'Yes';
    if (signUpCompleted) {
      signUpCompleted({
        utagAccountSource,
        newsLetter: newsLetterVal,
      });
    }
    if (signupSuccess) signupSuccess();
    if (!getUrlParameter('code'))
      handleAnalytics({
        event: Events.PROFILE_SIGNUP_COMPLETED,
        payload: {
          account: newAccount,
          page_profile_type: fbAuthStarted
            ? SegmentPageProfileTypes.FACEBOOK
            : SegmentPageProfileTypes.GENERAL,
          page_start_source: utagAccountSource,
        },
        tealiumPayload: {
          ...getCommonTealiumProps(),
        },
      });
    if (handleActivateCode({ ...newAccount, isNew: true })) return;
    toggleSignupSuccess();
  };
  setSignupErrors(
    hasErrors => {
      if (hasErrors || !register) {
        setIsProcessing(false);
        return;
      }
      const code = getActivateCodeFromURL() || getActivateCode();
      const { deviceId } = anonymousAccount || {};
      const toMigrate = deviceId ? { deviceId, migrateData: true } : {};
      const secondScreen = code ? { regcode: code } : {};
      const body = {
        password,
        email,
        newsLetter: true,
        ...toMigrate,
        ...secondScreen,
      };

      register(body, result => {
        setIsProcessing(false);
        const { error, account: newAccount } = result;
        const errorCode = _.get(error, 'errorCode');
        const errorCodeString = _.toString(errorCode);
        if (error) {
          handleAnalytics({
            event: Events.PROFILE_SIGN_UP_ERROR,
            payload: {
              page_error_description: error.message || 'no error description',
              page_error_type: 'server-side',
              page_start_source: utagAccountSource,
            },
          });
        }
        console.log('errorCodeString', errorCodeString);
        console.log('invalidEmailError', invalidEmailError);
        switch (errorCodeString) {
          case EMAIL_INVALID:
            return setGenericError(invalidEmailError);
          default:
            break;
        }
        return handleSuccess(newAccount);
      });
    },
    {
      setPasswordError,
      password,
      email,
      utagAccountSource,
    },
  );
};

const setSignupErrors = (cb, name) => {
  const { setPasswordError, password, email, utagAccountSource } = name;
  const signupPasswordError = passwordErrors(password);
  const signupEmailError = !isValidEmail(email);
  const missingFieldDataError = signupPasswordError || signupEmailError;
  const setStateCb = () => {
    const hasErrors = missingFieldDataError || false;
    if (hasErrors) {
      const errorDescription = 'Missing Field Data';
      handleAnalytics({
        event: Events.PROFILE_SIGN_UP_ERROR,
        payload: {
          page_error_description: errorDescription,
          page_error_type: 'client-side',
          page_start_source: utagAccountSource,
        },
      });
    }
    if (cb) return cb(hasErrors);
    return hasErrors;
  };

  if (signupPasswordError) setPasswordError(signupPasswordError);
  return setStateCb();
};
